import { Injectable } from '@angular/core';
import { applicationConstants } from 'src/app/shared/applicationConstants';
import { LocalStorageService } from 'src/app/shared/services/storage/local-storage.service';
import { SessionStorageService } from 'src/app/shared/services/storage/session-storage.service';
import { environment } from '../../../../../src/environments/environment';
import { DeviceHandlerService } from '../../../shared/services/device-handler.service';
import { SessionService } from '../../../shared/services/session.service';
import { WindowRefService } from '../../../shared/services/windowRef.service';
import { UserCategoryType } from '../../../shared/utils/SubscriptionUtil';
import {
  AppInfo,
  AsnInfo,
  BaseSubscription,
  BrowserInfo,
  BrowserName,
  DeviceInfo,
  EventInfo,
  Language,
  LanguageInfo,
  TimeInfo,
  UserAgentOs,
  UserInfo,
  UserRegionInfo,
  UserType,
} from '../model/newrelic.model';
import { DataService } from './../../../data.service';
import { KEYS } from 'src/app/shared/utils/constants/storage.constants';
import { getPreferredLanguages } from 'src/app/shared/utils/deviceUtils';

declare const window: Window &
  typeof globalThis & {
    tizenVersion: any;
    webOsVersion: any;
    deviceModel: any;
  };
@Injectable({
  providedIn: 'root',
})
export class NewrelicStore {
  private customAttributes = {};
  constructor(
    private data: DataService,
    private readonly windowRef: WindowRefService,
    private readonly localStorage: LocalStorageService,
    private readonly session: SessionService,
    private readonly sessionStorageService: SessionStorageService,
    private readonly deviceHandler: DeviceHandlerService
  ) {}

  public getCustomAttributes(): any {
    return this.customAttributes;
  }

  public setCustomAttributes(attrs: any): void {
    this.customAttributes = attrs;
  }

  public clearCustomAttributes(): void {
    this.customAttributes = {};
  }

  /**
   * Provides the browser details
   */
  public getBrowserInfo(): BrowserInfo {
    return {
      userAgentName: this.detectBrowserName(),
      userAgentOS: this.getUserAgentOS(),
      userAgentVersion: this.getUserAgentVersion(),
    };
  }

  public getEventInfo(): EventInfo {
    const startTime = new Date();
    if (this.sessionStorageService.getItem('confStart')) {
      startTime.setTime(
        Number(this.sessionStorageService.getItem('confStart'))
      );
    }
    return {
      appName: environment.app.appName,
      // session: this.session.getSessionID(),
      // timeSinceLoad: new Date().getTime() - startTime.getTime(),
      eventTimestamp: new Date().valueOf(),
    };
  }

  public getUserRegionInfo(): UserRegionInfo {
    let objLocation = JSON.parse(localStorage?.getItem(KEYS.LOCATION) || '{}');
    const region = this.getRegion();
    const userCity = this.getCity();
    if (objLocation === null || objLocation === undefined) {
      // Default location
      objLocation = {
        city: 'chennai',
        userCity: 'chennai',
        countryCode: 'IN',
        region: 'tn',
      };
    }
    delete objLocation.region;
    return { ...objLocation, regionCode: region, userCity };
  }

  public getDeviceInfo(): DeviceInfo {
    const newTxt = navigator.userAgent.split('(');
    const useragentdata = [];
    for (let i = 1; i < newTxt.length; i++) {
      useragentdata.push(newTxt[i].split(')')[0]);
    }

    const defaultModel = navigator.userAgent.includes('Web0S')
      ? 'Web0S'
      : 'Tizen';

    return {
      device: useragentdata[0],
      deviceID: this.deviceHandler.getCurrentDeviceID(),
      deviceGroup: 'TV',
      deviceManufacturer: navigator.userAgent.includes('Web0S')
        ? 'LG Electronics Inc'
        : 'Samsung',
      deviceModel: window.deviceModel ? window.deviceModel : defaultModel,
      deviceName: applicationConstants.DEVICE_NAME,
      deviceType: navigator.userAgent.includes('Web0S')
        ? 'lgwebostv'
        : 'samsungtizentv',
    };
  }

  public getTimeInfo(): TimeInfo {
    let intTimeToLoadStorefront = 0;
    let splashTime: number;
    let splashTimeEnd: number;
    let appStartUpTime = 0;

    try {
      this.data.storefrontTime =
        new Date().getTime() - this.data.storefrontTime;
      intTimeToLoadStorefront = sessionStorage.getItem('elapsedStorefront')
        ? parseInt(sessionStorage.getItem('elapsedStorefront'), 10)
        : 0;
      splashTime = sessionStorage.getItem('elapsedSplash')
        ? parseInt(sessionStorage.getItem('elapsedSplash'), 10)
        : 0;
      splashTimeEnd = sessionStorage.getItem('splashScreenEnd')
        ? parseInt(sessionStorage.getItem('splashScreenEnd'), 10)
        : 0;
      appStartUpTime = sessionStorage.getItem('appStartUpTime')
        ? parseInt(sessionStorage.getItem('appStartUpTime'), 10)
        : 0;
    } catch (e) {
      intTimeToLoadStorefront = 0;
    }
    const timeToSplash = splashTimeEnd - splashTime;
    return {
      timeToAppConfig: this.data.configTime,
      timeToLoadStorefront: intTimeToLoadStorefront,
      timeToSplash: timeToSplash > 5000 ? 5000 : timeToSplash,
      appStartUpTime,
    };
  }

  public getApplicationInfo(): AppInfo {
    return {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      appBuild: this.windowRef.nativeWindow.buildVersion,
      appVersion: this.windowRef.nativeWindow.appVersion,
    };
  }

  public getUserInfo(): UserInfo {
    const userID = this.localStorage.getItem(KEYS.CP_CUSTOMER_ID)
      ? this.localStorage.getItem(KEYS.CP_CUSTOMER_ID)
      : '';
    const baseSubscriptionValue =
      this.localStorage.getItem(KEYS.SUBSCRIPTION_PRODUCT_CODES) ||
      this.localStorage.getItem(KEYS.SUBSCRIPTION_PACKAGE);

    const profileID = localStorage.getItem(KEYS.SELECTED_PROFILE_ID)
      ? localStorage.getItem(KEYS.SELECTED_PROFILE_ID)
      : '';
    const userEntitlements = this.localStorage.getItem(
      KEYS.SUBSCRIPTION_STATUS
    );
    const userTypeValue =
      userEntitlements === 'true' ? UserType.SUBSCRIBED : UserType.REGISTERED;
    const baseSubscription = this.determineBaseSubscription(
      userEntitlements,
      baseSubscriptionValue
    );
    return {
      userID,
      profileID,
      userType: userID ? userTypeValue : UserType.ANONYMOUS,
      userCategory: this.getUserCategory(),
      baseSubscription,
      silentLogin:
        this.localStorage.getItem(KEYS.EVERGENT_ACCESS_TOKEN) !== null
          ? true
          : false,
    };
  }

  private determineBaseSubscription(
    userEntitlements: string | null,
    baseSubscriptionValue: string | null
  ): string {
    const isSubscribed = userEntitlements === 'true';
    const hasValidPackage = !!baseSubscriptionValue;

    return isSubscribed && hasValidPackage
      ? baseSubscriptionValue
      : BaseSubscription.NO_SUBSCRIPTION;
  }

  public getAsnInfo(): AsnInfo {
    const startTime = new Date();
    if (sessionStorage.getItem('confStart')) {
      startTime.setTime(Number(sessionStorage.getItem('confStart')));
    }
    return {
      asn: '10796',
      asnOwner: 'Time Warner Cable Internet LLC',
    };
  }

  public getLanguageInfo(): LanguageInfo {
    const contentLanguage =
      localStorage?.getItem(KEYS.WATCH_LANGUAGE) === 'te'
        ? Language.TELUGU
        : Language.TAMIL;
    const displayLanguage = localStorage.getItem('lang');
    const getDisplayLanguage = () => {
      if (displayLanguage === 'te') {
        return Language.TELUGU;
      }
      return displayLanguage === 'en' ? Language.ENGLISH : Language.TAMIL;
    };
    return {
      contentLanguage: Language.TELUGU + ',' + Language.TAMIL,
      // displayLanguage: getDisplayLanguage(),
      preferredLanguage: getPreferredLanguages(
        this.data.isFromDeeplink
          ? this.localStorage.getItem(KEYS.DL_PREFERRED_LANGUAGES)
          : null
      ),
    };
  }

  private detectBrowserName(): BrowserName {
    const agent = this.windowRef.nativeWindow.navigator.userAgent.toLowerCase();
    switch (true) {
      case agent.indexOf('edge') > -1:
        return BrowserName.edge;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      case agent.indexOf('opr') > -1 && !!this.windowRef.nativeWindow.opr:
        return BrowserName.opera;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      case agent.indexOf('chrome') > -1 && !!this.windowRef.nativeWindow.chrome:
        return BrowserName.chrome;
      case agent.indexOf('trident') > -1:
        return BrowserName.ie;
      case agent.indexOf('firefox') > -1:
        return BrowserName.firefox;
      case agent.indexOf('safari') > -1:
        return BrowserName.safari;
      default:
        return BrowserName.others;
    }
  }
  private getUserAgentVersion(): string {
    const userAgent = this.windowRef.nativeWindow.navigator.userAgent;
    let tem;
    let matchTest =
      userAgent.match(
        /(opera|chrome|safari|firefox|msie|trident(?=\/))\/?(\d{1,6})/i
      ) || [];
    if (/trident/i.test(matchTest[1])) {
      tem = /\brv[ :]+(\d{1,6})/g.exec(userAgent) || [];
      return 'IE ' + (tem[1] || '');
    }
    if (matchTest[1] === 'Chrome') {
      tem = userAgent.match(/\b(OPR|Edge)\/(\d+)/);
      if (tem != null) {
        return tem.slice(1).join(' ').replace('OPR', 'Opera');
      }
    }
    matchTest = matchTest[2]
      ? [matchTest[1], matchTest[2]]
      : [
          this.windowRef.nativeWindow.navigator.appName,
          this.windowRef.nativeWindow.navigator.appVersion,
          '-?',
        ];
    if ((tem = userAgent.match(/version\/(\d+)/i)) != null)
      matchTest.splice(1, 1, tem[1]);
    return matchTest.join(' ');
  }

  private getUserAgentOS(): UserAgentOs {
    const appVersion = this.windowRef?.nativeWindow?.navigator?.appVersion;
    switch (true) {
      case appVersion.indexOf('Win') !== -1:
        return UserAgentOs.Windows;
      case appVersion.indexOf('Mac') !== -1:
        return UserAgentOs.MacOS;
      case appVersion.indexOf('X11') !== -1:
        return UserAgentOs.Unix;
      case appVersion.indexOf('Linux') !== -1:
        return UserAgentOs.Linux;
      case appVersion.indexOf('Android') !== -1:
        return UserAgentOs.Android;
      default:
        return UserAgentOs.Windows;
    }
  }

  private getUserCategory(): string {
    return this.localStorage.getItem(KEYS.SUBSCRIPTION_PACKAGE) || undefined;
    /* let userCategory: string;
    let subPackage = localStorage.getItem('subscriptionPackage');
    if (subPackage) {
      subPackage = subPackage?.toUpperCase();
      if (subPackage.includes('TELUGU')) {
        userCategory = UserCategoryType.TELUGU;
      } else if (subPackage.includes('TAMIL')) {
        userCategory = UserCategoryType.TAMIL;
      } else if (subPackage.includes('GOLD')) {
        userCategory = UserCategoryType.GOLD;
      } else {
        userCategory = UserCategoryType.NA;
      }
    } else {
      userCategory = UserCategoryType.NA;
    }
    return userCategory; */
  }

  private getCity() {
    return this.localStorage.getItem(KEYS.CITY);
  }

  private getRegion() {
    return this.localStorage.getItem(KEYS.REGION);
  }
}
